import * as React from "react";
import styles from "./styles.module.css";
import { useQuery } from "@tanstack/react-query";
import {
  getPatientGeneralDetails,
  getPatientReadings,
  fetchPatientReadingEvents
} from "../../../services/patient/details";
import { formatDate } from "../../../utils/helper";
import { formatUSADate, formatUSATimestamp } from "../../../utils/dateFormatters";
import HealthChart from "../../shared-modules/HealthChart";
import Button from "../../button-v1";
import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import html2canvas from "html2canvas";
import toast from "react-hot-toast";
import { useSearchParams } from "react-router-dom";
import logo from "../../../assets/images/toca_health_pdf_logo.png";
import ReadingReportPDF from "./ReadingReportPDF";
import Spinner from "../../spinner";
import { buildReportFilename, DOCTYPES } from "../../../utils/fileNaming";

const ReadingView = () => {
  const [params] = useSearchParams();
  const patientId = sessionStorage.getItem("_pt_id");
  const locationName = JSON.parse(sessionStorage.getItem("current_loc"))?.name;
  const [timeFrame, setTimeFrame] = React.useState(null);
  const [readingType, setReadingType] = React.useState(null);
  const [dateRange, setDateRange] = React.useState({});
  const [downloading, setDownloading] = React.useState(false);

  const chartRef = React.useRef(null);

  const { data: patient, isLoading: isPatientLoading } = useQuery(
    ["patient-general-details"],
    () => getPatientGeneralDetails()
  );

  const { data: data, isLoading: isReadingLoading } = useQuery(
    ["patient-readings", patientId, timeFrame, readingType, dateRange],
    () => getPatientReadings(patientId, readingType, timeFrame, dateRange)
  );

  const { data: events } = useQuery(
    ["patient-reading-events", readingType, patientId],
    () => fetchPatientReadingEvents(readingType),
    { enabled: !!patientId && !!readingType }
  );

  const handleRange = () => {
    const end = new Date();
    let start = new Date();

    if (timeFrame === "2days") {
      start.setDate(end.getDate() - 2);
    } else if (timeFrame === "1week") {
      start.setDate(end.getDate() - 7);
    } else if (timeFrame === "1month") {
      start.setMonth(end.getMonth() - 1);
    } else if (timeFrame === "3months") {
      start.setMonth(end.getMonth() - 3);
    } else if (timeFrame === "6months") {
      start.setMonth(end.getMonth() - 6);
    } else if (timeFrame === "1year") {
      start.setFullYear(end.getFullYear() - 1);
    } else if (dateRange.startDate && dateRange.endDate) {
      // for the custom Date Range
      return `${formatUSADate(dateRange.startDate)} - ${formatUSADate(
        dateRange.endDate
      )}`;
    } else {
      return "_";
    }

    return `${formatUSADate(start)} - ${formatUSADate(end)}`;
  };

  React.useEffect(() => {
    const tf = params.get("timeFrame");
    const rt = params.get("readingType");
    const rs = params.get("rangeStart");
    const re = params.get("rangeEnd");
    if (tf) setTimeFrame(tf);
    if (rs && re)
      setDateRange({ startDate: new Date(rs), endDate: new Date(re) });
    if (rt) setReadingType(rt);
  }, [params]);

  const handleDownloadPDF = async () => {
    setDownloading(true);
    const toastId = toast.loading("Generating PDF...");

    const originalStyles = new Map();

    try {
      let chartImage = null;

      if (chartRef.current) {
        const settingsGrid = chartRef.current.querySelector(
          "#health-chart-settings-grid"
        );
        let originalDisplay = "";

        if (settingsGrid) {
          originalDisplay = settingsGrid.style.display;
          settingsGrid.style.display = "none";
        }

        const bpSections = chartRef.current.querySelectorAll(
          '[class*="bpSummarySection"]'
        );
        bpSections.forEach(section => {
          originalStyles.set(section, section.style.cssText);
          section.style.padding = "0.5rem 1rem";
          section.style.marginBottom = "0.5rem";
        });

        const bpSummaryDetails = chartRef.current.querySelectorAll(
          '[class*="bpSummaryDetails"]'
        );
        bpSummaryDetails.forEach(section => {
          originalStyles.set(section, section.style.cssText);
          section.style.borderTop = "none";
        });

        const barContainers = chartRef.current.querySelectorAll(
          '[class*="bpBarContainer"]'
        );
        barContainers.forEach(bar => {
          originalStyles.set(bar, bar.style.cssText);
          bar.style.marginBottom = "1.2rem";
        });

        const categoryBars = chartRef.current.querySelectorAll(
          '[class*="bpCategoryBar"]'
        );
        categoryBars.forEach(bar => {
          originalStyles.set(bar, bar.style.cssText);
          bar.style.margin = "0.5rem 0";
        });
        const allLegendDots = chartRef.current.querySelectorAll(
          '[class*="legendDot"], [class*="bpLegendDot"], [class*="normalLegendDot"], [class*="abnormalLegendDot"], [class*="criticalLegendDot"], div[style*="border-radius: 50%"]'
        );

        allLegendDots.forEach(dot => {
          originalStyles.set(dot, dot.style.cssText);

          dot.style.marginTop = "0px";
          dot.style.marginBottom = "2rem";
          dot.style.transform = "translateY(1px)";
        });

        const sourceNotes = chartRef.current.querySelectorAll(
          '[class*="bpSourceNote"]'
        );

        sourceNotes.forEach(note => {
          if (!originalStyles.has(note)) {
            originalStyles.set(note, note.style.cssText);
          }
          note.style.borderTop = "none";
        });

        await new Promise(resolve => setTimeout(resolve, 600));

        const canvas = await html2canvas(chartRef.current, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff"
        });
        chartImage = canvas.toDataURL("image/png");

        originalStyles.forEach((cssText, element) => {
          element.style.cssText = cssText;
        });

        if (settingsGrid) {
          settingsGrid.style.display = originalDisplay;
        }
      }

      const blob = await pdf(
        <ReadingReportPDF
          patient={patient}
          readings={data?.readings}
          locationName={locationName}
          rangeLabel={handleRange()}
          logo={logo}
          chartImage={chartImage}
          readingCount={data?.readings?.length || 0}
        />
      ).toBlob();

      const fileName = buildReportFilename({
        patientFirstName: patient?.user?.first_name,
        patientLastName: patient?.user?.last_name,
        doctype: DOCTYPES.REPORT,
        program: "RPM",
        ext: "pdf",
      });
      saveAs(blob, fileName);

      toast.success("PDF Downloaded!", { id: toastId });
    } catch (error) {
      console.error("PDF generation failed:", error);
      toast.error("Failed to generate PDF", { id: toastId });

      if (chartRef.current) {
        originalStyles.forEach((cssText, element) => {
          element.style.cssText = cssText;
        });
        const settingsGrid = chartRef.current.querySelector(
          "#health-chart-settings-grid"
        );
        if (settingsGrid) settingsGrid.style.display = "";
      }
    } finally {
      setDownloading(false);

      if (chartRef.current) {
        const allLegendDots = chartRef.current.querySelectorAll(
          '[class*="legendDot"], [class*="bpLegendDot"], div[style*="border-radius: 50%"]'
        );
        allLegendDots.forEach(dot => {
          dot.style.marginBottom = "0px";
        });
      }
    }
  };

  if (isPatientLoading || isReadingLoading)
    return (
      <div className={styles.loadingContainer}>
        <Spinner />
      </div>
    );

  const flattenedReadings = [];
  data?.readings?.forEach(reading => {
    let type = reading.reading_type_display || reading.reading_type;
    let val = "-";
    if (reading.reading_type === "blood_pressure")
      val = `${reading.systolic}/${reading.diastolic} mmHg`;
    else if (reading.reading_type === "weight") val = `${reading.weight} kg`;
    else if (reading.reading_type === "temperature")
      val = `${reading.temperature} °F`;
    else if (reading.reading_type === "blood_glucose")
      val = `${reading.glucose} mg/dL`;
    else if (reading.reading_type === "pulse_ox") val = `${reading.oxygen}%`;

    flattenedReadings.push({
      ...reading,
      reading,
      type,
      value: val,
      isHeartRate: false
    });
    if (reading.heart_rate) {
      flattenedReadings.push({
        ...reading,
        reading,
        type: "Heart Rate",
        value: `${reading.heart_rate} bpm`,
        isHeartRate: true
      });
    }
  });

  const getRowClass = item => {
    const cat =
      (item.isHeartRate
        ? item.heart_rate_category
        : item.bp_category
      )?.toLowerCase() || "";
    if (cat.includes("crisis")) return styles.rowCrisis;
    if (cat.includes("hypo")) return styles.rowHypotensive;
    if (cat.includes("normal")) return styles.rowNormal;
    if (cat.includes("elevated")) return styles.rowElevated;
    if (cat.includes("stage 1") || cat.includes("bradycardia"))
      return styles.rowStage1;
    if (cat.includes("stage 2") || cat.includes("tachycardia"))
      return styles.rowStage2;
    return "";
  };

  const getCategoryText = item => {
    let cat =
      (item.isHeartRate ? item.heart_rate_category : item.bp_category) || "-";
    if (cat === "Stage 1") return "Stage 1 Hypertension";
    if (cat === "Stage 2") return "Stage 2 Hypertension";
    return cat;
  };

  return (
    <>
      <section className={styles.download_button}>
        <Button loading={downloading} onClick={handleDownloadPDF}>
          Download PDF
        </Button>
      </section>

      <main className={styles.container} id="reading-pdf-view">
        <section className={styles.header}>
          <img src={logo} alt="Logo" className={styles.logo} />
          <div className={styles.separator}></div>
          <p className={styles.locationName}>{locationName || "_"}</p>
          <div className={styles.titleRow}>
            <h1 className={styles.reportTitle}>Remote Monitoring Report</h1>
            <span className={styles.reportDate}>{handleRange()}</span>
          </div>
        </section>

        <section className={styles.patientInfoGrid}>
          <div>
            <div className={styles.fieldGroup}>
              <label className={styles.label}>Patient Name:</label>
              <span className={styles.value}>
                {patient?.user?.first_name} {patient?.user?.last_name}
              </span>
            </div>
            <div className={styles.fieldGroup}>
              <label className={styles.label}>DOB:</label>
              <span className={styles.value}>
                {formatUSADate(patient?.user?.dob)}
              </span>
            </div>
            <div className={styles.fieldGroup}>
              <label className={styles.label}>Number of readings:</label>
              <span className={styles.value}>
                {data?.readings?.length || 0}
              </span>
            </div>
          </div>

          <div>
            <div className={styles.fieldGroup}>
              <label className={styles.label}>Doctor:</label>
              <span className={styles.value}>
                {patient?.provider?.first_name} {patient?.provider?.last_name}
              </span>
            </div>
            <div className={styles.fieldGroup}>
              <label className={styles.label}>Subaccount Name:</label>
              <span className={styles.value}>{locationName || "_"}</span>
            </div>
          </div>
        </section>

        <h3 className={styles.graphTitle} style={{ padding: "10px 30px 0" }}>
          RPM Graph
        </h3>

        <section className={styles.chart_container} ref={chartRef}>
          <HealthChart
            readingType={readingType}
            timeFrame={timeFrame}
            dateRange={dateRange}
            readings={data?.readings ?? []}
            events={events || []}
            bpStats={data?.bp_stats}
            glucoseStats={data?.stats?.glucose_stats}
            pulseOxStats={data?.stats?.pulse_ox_stats}
            temperatureStats={data?.stats?.temperature_stats}
            weightStats={data?.stats?.weight_stats}
          />
        </section>

        <section className={styles.table_container}>
          <table className={styles.table}>
            <thead className={styles.tableHeader}>
              <tr>
                <th>Timestamp</th>
                <th>Type</th>
                <th>Reading</th>
                <th>Category</th>
              </tr>
            </thead>
            <tbody>
              {flattenedReadings.map((item, i) => (
                <tr
                  key={i}
                  className={`${styles.tableRow} ${getRowClass(item)}`}
                >
                  <td className={styles.tableCell}>
                    {formatUSATimestamp(item.recorded_at)}
                  </td>
                  <td className={styles.tableCell}>{item.type}</td>
                  <td className={styles.tableCell}>{item.value}</td>
                  <td className={styles.tableCell}>{getCategoryText(item)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <footer className={styles.footer}>
          <span>
            {patient?.user?.first_name} {patient?.user?.last_name} - Remote Monitoring Report
          </span>
          <span>1 of 1</span>
          <span> {new Date().toLocaleString("en-US", {
              month: "2-digit",
              day: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              hour12: true
            })}</span>
        </footer>
      </main>
    </>
  );
};

export default ReadingView;
